import React from 'react'
import styled, { css } from 'styled-components'
import { useSelector } from 'react-redux'
import PlaceholderUser from 'assets/placeholder-user.png'
import Icon from 'components/Icon'
import { _, colors, formatName } from 'utils'
import { useEventSlug, useDeleteItem } from 'hooks'
import { currentUserSelector } from '../auth/authSlice'
import { removeAdmin } from './graphql/mutation'
import { AdminType } from './EventAdmins'

const S = {
    RowWrapper: styled.div<{ hasLoggedOn: boolean, isCurrentUser: boolean }>`
        position: relative;
        display: grid;
        align-items: center;
        grid-template-columns: 1fr 1fr 20px;
        width: calc(100% + 20px);
        margin: 0 -10px;
        padding: 8px 10px;
        border-bottom: 1px solid ${colors.gray1};
        & > .delete-icon {
            display: none;
        }
        &:hover .delete-icon {
            display: block;
        }
        ${({ hasLoggedOn }) => !hasLoggedOn && css`
            background-color: ${colors.iconOpacity(0.05)};
            border-top: 1px dashed rgba(120, 144, 156, 0.25);
            border-bottom: 1px dashed rgba(120, 144, 156, 0.25);
        `}
        ${({ isCurrentUser }) => isCurrentUser && css`
            background-color: ${colors.iconOpacity(0.03)};
            & .admin-status {
                opacity: 0;
            }
            &:hover .admin-status {
                opacity: 1;
            }
        `}
    `,
    Info: styled.div`
        display: flex;
        align-items: center;
        min-width: 0;
        padding-right: 20px;
    `,
    Thumbnail: styled.img`
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
        object-position: center;
        margin-right: 8px;
    `,
    Name: styled.span`
        color: ${colors.darkOpacity(0.9)};
    `,
    Status: styled.span`
        font-weight: bold;
        font-size: 14px;
        text-transform: uppercase;
        color: ${colors.iconOpacity(0.5)};
    `
}

interface Props {
    admin: AdminType
    callGetAdmins: () => Promise<void>
}

const AdminRow = ({ admin, callGetAdmins }: Props) => {
    const eventSlug = useEventSlug()
    const currentUser = useSelector(currentUserSelector)
    const deleteFn = (id) => removeAdmin(eventSlug, id)
    const { showDeleteConfirmModal } = useDeleteItem(
        deleteFn, callGetAdmins
    )
    const isCurrentUser = currentUser.id === admin.id

    return (
        <S.RowWrapper hasLoggedOn={admin.hasLoggedOn} isCurrentUser={isCurrentUser}>
            <S.Info>
                <S.Thumbnail src={admin.mugshot || PlaceholderUser} />
                <S.Name className="text-cut">
                    {formatName({ first_name: admin.firstName, last_name: admin.lastName }) || admin.email}
                </S.Name>
            </S.Info>
            <S.Status className="admin-status">
                {!admin.hasLoggedOn ? _('event_admins.pending') : ''}
                {isCurrentUser ? _('event_admins.is_you') : ''}
            </S.Status>
            {!isCurrentUser && (
                <Icon
                    className="delete-icon"
                    icon="trash"
                    size={18}
                    onClick={() => showDeleteConfirmModal(admin.id)}
                />
            )}
        </S.RowWrapper>
    )
}

export default AdminRow
